import * as Sentry from '@sentry/nextjs';
import type { SP500Constituent } from 'utils/shared/sp500Constituents';
export type { SP500Constituent } from 'utils/shared/sp500Constituents';
import { differenceInMinutes, parseISO } from 'date-fns';
import {
  CompanyProfile,
  CompanyQuote,
  StockNews,
  TreasuryRates,
  PriceTargetConsensus,
  UpdownGradeConsensus,
} from 'constants/stockApi';
import { SP500_CONSTITUENTS_CACHE_KEY } from 'constants/cache';
import { getCache, setCache } from 'services/server/cache';
import client, { clientV4, clientStable } from './client';
import { HISTORY_PROXIES, INDEX_PROXIES, toIndexLevel, toIndexQuote } from './indexProxies';
import { fetchSP500ConstituentsFromWikipedia, isValidSP500Constituents } from './wikipedia';

type CalendarType =
  | 'earnings'
  | 'earnings-calendar'
  | 'economic-calendar'
  | 'ipos-calendar'
  | 'dividends'
  | 'splits';

export interface TickerProfile {
  symbol: string;
  companyName: string;
  sector: string;
  industry: string;
  exchangeShortName: string;
  marketCap: number;
  country: string;
  isEtf: boolean;
  isFund: boolean;
  isActivelyTrading: boolean;
  ipoDate: string;
  website: string;
  description: string;
  ceo: string;
  employees: number;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  dcfDiff: number;
  dcf: number;
  image: string;
  currency: string;
  cik: string;
  cusip: string;
  isin: string;
}

export const fetchCompanyProfile = async (ticker: string) => {
  const resp = await client.get(`profile/${ticker}`);
  return resp as CompanyProfile[];
};

export const fetchCompanyQuote = async (ticker: string) => {
  const resp = await client.get(`quote/${ticker}`);
  return resp as CompanyQuote[];
};

/**
 * The quote for an index root such as SPX or NDX, or undefined when FMP has none.
 * SPX and DJX read the instrument in `INDEX_PROXIES`, because FMP refuses ^SPX and ^DJX.
 */
export const fetchIndexQuote = async (root: string): Promise<CompanyQuote | undefined> => {
  const proxy = INDEX_PROXIES[root];
  if (!proxy) {
    return (await fetchCompanyQuote(`^${root}`))[0];
  }
  const [quote] = await fetchCompanyQuote(proxy.source);
  return quote && toIndexQuote(quote, proxy);
};

export interface HistoricalPrice {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  adjClose: number;
  volume: number;
  unadjustedVolume: number;
  change: number;
  changePercent: number;
  vwap: number;
  label: string;
  changeOverTime: number;
}

export const fetchHistoricalPrice = async (
  ticker: string,
  date: string,
): Promise<number | null> => {
  try {
    // Convert date format from YYYY/MM/DD to YYYY-MM-DD
    const formattedDate = date.replace(/\//g, '-');

    // Fetch a small range around the date to handle weekends/holidays
    const targetDate = new Date(formattedDate);
    const fromDate = new Date(targetDate);
    fromDate.setDate(targetDate.getDate() - 5); // 5 days before
    const toDate = new Date(targetDate);
    toDate.setDate(targetDate.getDate() + 1); // 1 day after

    const from = fromDate.toISOString().split('T')[0];
    const to = toDate.toISOString().split('T')[0];

    const resp = await client.get(`historical-price-full/${ticker}`, {}, [
      `from=${from}`,
      `to=${to}`,
    ]);

    // API returns { symbol, historical: [...] }
    const data = resp as any;
    if (data && data.historical && data.historical.length > 0) {
      // Find the closest date to our target
      const historical = data.historical as HistoricalPrice[];
      const closest = historical.reduce((prev, curr) => {
        const prevDiff = Math.abs(new Date(prev.date).getTime() - targetDate.getTime());
        const currDiff = Math.abs(new Date(curr.date).getTime() - targetDate.getTime());
        return currDiff < prevDiff ? curr : prev;
      });

      return closest.close;
    }

    return null;
  } catch (error) {
    console.error(`Failed to fetch historical price for ${ticker} on ${date}:`, error);
    return null;
  }
};

/**
 * Fetch S&P 500 index value at a specific date
 * Reads ^XSP × 10, because FMP refuses ^GSPC (see `HISTORY_PROXIES`)
 */
export const fetchSP500HistoricalValue = async (date: string): Promise<number | null> => {
  const proxy = HISTORY_PROXIES['^GSPC'];
  const value = await fetchHistoricalPrice(proxy.source, date);
  return value === null ? null : toIndexLevel(value, proxy);
};

export const fetchStockNews = async (ticker: string, limit: number = 25) => {
  const resp = await client.get('stock_news', {}, [`tickers=${ticker}`, `limit=${limit}`]);
  return resp as StockNews[];
};

export const fetchMostRecentRiskFreeRates = async () => {
  const { today, lastWeek } = getDates();

  const resp = (await clientV4.get('treasury', {}, [
    `from=${lastWeek}`,
    `to=${today}`,
  ])) as TreasuryRates[];

  return resp;
};

export const fetchPriceTargetConsensus = async (ticker: string) => {
  const resp = await clientV4.get('price-target-consensus', {}, [`symbol=${ticker}`]);
  return resp as PriceTargetConsensus[];
};

export const fetchUpgradeDowngradeConsensus = async (ticker: string) => {
  const resp = await clientV4.get('upgrades-downgrades-consensus', {}, [`symbol=${ticker}`]);
  return resp as UpdownGradeConsensus[];
};

export const fetchSenateTrading = async (pageNo: number) => {
  const resp = await clientV4.get('senate-trading-rss-feed', {}, [`page=${pageNo}`]);
  return resp as any[];
};

export const fetchHouseDisclosure = async (pageNo: number) => {
  const resp = await clientV4.get('senate-disclosure-rss-feed', {}, [`page=${pageNo}`]);
  return resp as any[];
};

export interface CongressDisclosureStable {
  symbol: string; // e.g. "$BTCUSD"
  disclosureDate: string; // "YYYY-MM-DD"
  transactionDate: string; // "YYYY-MM-DD"
  firstName: string;
  lastName: string;
  office?: string;
  district?: string;
  owner?: string;
  assetDescription: string;
  assetType?: string; // e.g. "Cryptocurrency"
  type: string; // "Purchase" / "Sale" / etc.
  amount: string; // "$1,001 - $15,000"
  capitalGainsOver200USD?: string; // "True"/"False"
  comment?: string;
  link: string;
}

export const fetchSenateLatestStable = async (pageNo: number, limit = 100) => {
  const resp = await clientStable.get('senate-latest', {}, [`page=${pageNo}`, `limit=${limit}`]);
  return resp as CongressDisclosureStable[];
};

export const fetchHouseLatestStable = async (pageNo: number, limit = 100) => {
  const resp = await clientStable.get('house-latest', {}, [`page=${pageNo}`, `limit=${limit}`]);
  return resp as CongressDisclosureStable[];
};

export const fetchInsiderTrades = async (pageNo: number, symbol?: string) => {
  const queryParams = [`page=${pageNo}`];
  if (symbol) {
    queryParams.push(`symbol=${symbol}`);
  }

  const resp = await clientV4.get('insider-trading', {}, queryParams);

  return resp as any[];
};

export interface EarningsCalendar {
  symbol: string;
  date: string;
  epsActual: number | null;
  epsEstimated: number | null;
  revenueActual: number | null;
  revenueEstimated: number | null;
  lastUpdated: string;
}
export const fetchEarningsCalendar = async ({ from, to }: { from: string; to: string }) => {
  const resp = await clientStable.get('earnings-calendar', {}, [`from=${from}`, `to=${to}`]);
  return resp as EarningsCalendar[];
};

export interface SplitsCalendar {
  symbol: string;
  date: string;
  numerator: number;
  denominator: number;
}

export const fetchSplitsCalendar = async ({ from, to }: { from: string; to: string }) => {
  const resp = await clientStable.get('splits-calendar', {}, [`from=${from}`, `to=${to}`]);
  return resp as SplitsCalendar[];
};

export interface DividendsCalendar {
  symbol: string;
  date: string;
  recordDate: string;
  paymentDate: string;
  declarationDate: string;
  adjDividend: number;
  dividend: number;
  yield: number;
  frequency: string;
}

export const fetchDividendsCalendar = async ({ from, to }: { from: string; to: string }) => {
  const resp = await clientStable.get('dividends-calendar', {}, [`from=${from}`, `to=${to}`]);
  return resp as DividendsCalendar[];
};

export interface IpoCalendar {
  symbol: string;
  date: string;
  daa: string;
  company: string;
  exchange: string;
  actions: string;
  shares: number | null;
  priceRange: number | null;
  marketCap: number | null;
}

export const fetchIpoCalendar = async ({ from, to }: { from: string; to: string }) => {
  const resp = await clientStable.get('ipos-calendar', {}, [`from=${from}`, `to=${to}`]);
  return resp as IpoCalendar[];
};

export interface EconomicCalendar {
  date: string;
  country: string;
  event: string;
  currency: string;
  previous: number;
  estimate: number | null;
  actual: number;
  change: number;
  impact: string;
  changePercentage: number;
}

export const fetchEconomicCalendar = async ({ from, to }: { from: string; to: string }) => {
  const resp = await clientStable.get('economic-calendar', {}, [`from=${from}`, `to=${to}`]);
  return resp as EconomicCalendar[];
};

// Use API for market cap and confirm usage
export const fetchMarketCapBulk = async ({ tickers = [] }: { tickers: string[] }) => {
  const resp = await clientStable.get('market-capitalization-batch', {}, [
    `symbols=${tickers.join(',')}`,
  ]);
  return resp;
};

export const fetchPriceTargetsBulk = async (transformKeys = true) => {
  const resp = await clientStable.getCsv('price-target-summary-bulk', {}, [], transformKeys);
  return resp;
};

export const fetchAnalystRatingsBulk = async (transformKeys = true) => {
  const resp = await clientStable.getCsv(
    'upgrades-downgrades-consensus-bulk',
    {},
    [],
    transformKeys,
  );
  return resp;
};

export const fetchCalendarData = async (
  type: CalendarType,
  symbol?: string,
): Promise<EarningsCalendar[]> => {
  const queryParams = symbol ? [`symbol=${symbol}`] : [];
  return clientStable.get(type, {}, queryParams) as Promise<EarningsCalendar[]>;
};

export const fetchEarningsBySymbol = async (symbol?: string): Promise<EarningsCalendar[]> => {
  const queryParams = symbol ? [`symbol=${symbol}`] : [];
  return clientStable.get('earnings', {}, queryParams) as Promise<EarningsCalendar[]>;
};

export const fetchEconomicEventsBySymbol = async (symbol?: string): Promise<EconomicCalendar[]> => {
  const queryParams = symbol ? [`symbol=${symbol}`] : [];
  return clientStable.get('economic-calendar', {}, queryParams) as Promise<EconomicCalendar[]>;
};

export const fetchIPO = async (): Promise<IpoCalendar[]> => {
  return clientStable.get('ipos-calendar', {}, []) as Promise<IpoCalendar[]>;
};
export const fetchDividendsBySymbol = async (symbol?: string): Promise<DividendsCalendar[]> => {
  const queryParams = symbol ? [`symbol=${symbol}`] : [];
  return clientStable.get('dividends', {}, queryParams) as Promise<DividendsCalendar[]>;
};

export const fetchSplitsBySymbol = async (symbol?: string): Promise<SplitsCalendar[]> => {
  const queryParams = symbol ? [`symbol=${symbol}`] : [];
  return clientStable.get('splits', {}, queryParams) as Promise<SplitsCalendar[]>;
};

function getDates() {
  // Get today's date
  const today = new Date();

  // Format today's date
  const yyyy = today.getFullYear();
  const mm = today.getMonth() + 1; // January is 0!
  const dd = today.getDate();

  // Add leading zeros to month and day if necessary
  const mmString = mm < 10 ? '0' + mm : mm;
  const ddString = dd < 10 ? '0' + dd : dd;

  const formattedToday = `${yyyy}-${mmString}-${ddString}`;

  // Calculate the date one week ago
  const lastWeek = new Date(today);
  lastWeek.setDate(today.getDate() - 7);

  // Format the date one week ago
  const lastWeekYyyy = lastWeek.getFullYear();
  let lastWeekMm = lastWeek.getMonth() + 1;
  let lastWeekDd = lastWeek.getDate();

  const lastWeekMmString = lastWeekMm < 10 ? '0' + lastWeekMm : lastWeekMm;
  const lastWeekDdString = lastWeekDd < 10 ? '0' + lastWeekDd : lastWeekDd;

  const formattedLastWeek = `${lastWeekYyyy}-${lastWeekMmString}-${lastWeekDdString}`;

  return { today: formattedToday, lastWeek: formattedLastWeek };
}

export interface StockPriceChange {
  symbol: string;
  '1D': number;
  '5D': number;
  '1M': number;
  '3M': number;
  '6M': number;
  '1Y': number;
  '3Y': number;
  '5Y': number;
  '10Y': number;
  max: number;
}

export interface BatchQuote {
  symbol: string;
  name: string;
  price: number;
  changesPercentage: number;
  change: number;
  dayLow: number;
  dayHigh: number;
  yearHigh: number;
  yearLow: number;
  marketCap: number;
  priceAvg50: number;
  priceAvg200: number;
  exchange: string;
  volume: number;
  avgVolume: number;
  open: number;
  previousClose: number;
  eps: number;
  pe: number;
  earningsAnnouncement: string;
  sharesOutstanding: number;
  timestamp: number;
}

/**
 * Fetch S&P 500 constituents from Wikipedia, cached in Vercel KV.
 *
 * - 24h TTL
 * - Stale-on-error: if Wikipedia fails after the TTL elapses, the previous
 *   cache entry is returned and left in place. The cache is only ever
 *   replaced on a successful refresh, never cleared.
 */
export const fetchSP500Constituents = async (): Promise<SP500Constituent[]> => {
  const cached: { data: SP500Constituent[]; lastUpdated: string } | null = await getCache(
    SP500_CONSTITUENTS_CACHE_KEY,
  );
  const now = new Date();

  const validCached = isValidSP500Constituents(cached?.data);
  const age =
    typeof cached?.lastUpdated === 'string'
      ? differenceInMinutes(now, parseISO(cached.lastUpdated))
      : NaN;
  const isFresh = validCached && age >= 0 && age < 24 * 60;

  if (isFresh) {
    return cached!.data;
  }

  const fresh = await fetchSP500ConstituentsFromWikipedia();

  if (isValidSP500Constituents(fresh)) {
    await setCache(SP500_CONSTITUENTS_CACHE_KEY, {
      data: fresh,
      lastUpdated: now.toISOString(),
    });
    return fresh;
  }

  // Wikipedia refresh failed. Sentry already captures the underlying error,
  // but log a business-level event so we know constituents are going stale.
  Sentry.captureMessage('Failed to refresh S&P 500 constituents from Wikipedia', {
    level: Sentry.Severity.Warning,
    tags: { source: 'sp500-constituents' },
    extra: {
      fellBackToStale: validCached,
      staleSince: cached?.lastUpdated || null,
      cachedCount: cached?.data?.length || 0,
    },
  });

  if (validCached) {
    return cached!.data;
  }

  return [];
};

export const fetchStockPriceChanges = async (symbols: string[]) => {
  const symbolString = symbols.join(',');
  const resp = await clientStable.get('stock-price-change', {}, [`symbol=${symbolString}`]);
  return resp as StockPriceChange[];
};

export const fetchBatchQuotes = async (symbols: string[]) => {
  const symbolString = symbols.join(',');
  const resp = await clientStable.get('batch-quote', {}, [`symbols=${symbolString}`]);
  return resp as BatchQuote[];
};

export const fetchAllPages = async <T>(
  fetchFunction: (page: number) => Promise<T[]>,
): Promise<T[]> => {
  let allData: T[] = [];
  let page = 0;
  let fetchMore = true;

  while (fetchMore) {
    try {
      const result = await fetchFunction(page);
      if (result.length > 0) {
        allData = allData.concat(result);
        page++;
      } else {
        fetchMore = false;
      }
    } catch (error) {
      console.error(`Error fetching data for page ${page}:`, error);
      fetchMore = false;
    }
  }
  return allData;
};
